import {
  IAIWorkspace,
  IAIWorkspaceRequest,
  useCreateScriptVetRequest,
} from "@entities/aiWorkspace";
import { Collapse } from "antd";
import { formatDistanceToNow } from "date-fns";
import { ArrowRight, Clock, Loader2 } from "lucide-react";
import pageStyles from "../styles.module.scss";
import { aiWorkspaceRequestId } from "../useAiWorkspaceModuleParams";
import { useLocalRequestPrompt } from "../useLocalRequestPrompt";
import { WorkspacePromptEditor } from "../WorkspacePromptEditor";
import {
  ModuleProgress,
  SCRIPT_VET_STEPS,
  vetListStep,
} from "../ModuleProgress";
import { promptToText } from "../WorkspacePromptEditor/promptToText";
import styles from "./styles.module.scss";

function firstLine(value: string) {
  return (
    promptToText(value)
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.length > 0) || ""
  );
}

function trimmedScript(value: string) {
  const line = firstLine(value);
  return line.length > 72 ? `${line.slice(0, 72).trimEnd()}…` : line;
}

type ScriptVetListProps = {
  workspace: IAIWorkspace;
  requests: IAIWorkspaceRequest[];
  onOpenRequest: (requestId: string, type: string) => void;
};

export function ScriptVetList({
  workspace,
  requests,
  onOpenRequest,
}: ScriptVetListProps) {
  const { promptHtml, initialHtml, updatePrompt, contentKey } =
    useLocalRequestPrompt(
      workspace._id,
      "script-vet",
      workspace.scriptVetMasterPrompt?.prompt || ""
    );
  const { create, isCreating } = useCreateScriptVetRequest((request) => {
    const id = aiWorkspaceRequestId(request);
    if (id) onOpenRequest(id, "script-vet");
  });
  const scriptRequests = requests.filter(
    (request) => request.type === "script-vet"
  );

  return (
    <div className={styles.tab}>
      <ModuleProgress
        kicker="Review"
        title="Script Vet"
        subtitle="Check a concept and scene-by-scene script against one shared rubric before approval."
        steps={SCRIPT_VET_STEPS}
        current={vetListStep(promptHtml, scriptRequests.length)}
      />
      <WorkspacePromptEditor
        moduleId="script-vet"
        aiWorkspaceId={workspace._id}
        label="Request prompt"
        hint="Kept on this page until you add a script check."
        savedPrompt={initialHtml}
        contentKey={contentKey}
        isSaving={false}
        requestOnly
        onHtmlChange={updatePrompt}
      />
      <Collapse
        className={pageStyles.requestsCollapse}
        defaultActiveKey={[]}
        items={[
          {
            key: "requests",
            label: (
              <div className={pageStyles.collapseLabel}>
                <span className={pageStyles.collapseTitle}>Requests</span>
                <span className={pageStyles.collapseCount}>
                  {scriptRequests.length}
                </span>
              </div>
            ),
            extra: (
              <button
                type="button"
                className={pageStyles.collapseActionBtn}
                disabled={isCreating}
                onClick={(e) => {
                  e.stopPropagation();
                  create({
                    aiWorkspaceId: workspace._id,
                    masterPrompt: promptHtml,
                  });
                }}
              >
                {isCreating ? (
                  <Loader2 size={13} className="spin-animation" />
                ) : null}
                New script check
              </button>
            ),
            children: (
              scriptRequests.length === 0 ? (
                <div className={pageStyles.emptyRequests}>
                  <p className={pageStyles.emptyTitle}>No scripts yet</p>
                  <p className={pageStyles.emptySub}>
                    Start a new script check to review one.
                  </p>
                </div>
              ) : (
                <div className={pageStyles.requestsList}>
                  {scriptRequests.map((request) => (
                    <button
                      key={request._id || request.id}
                      type="button"
                      className={pageStyles.requestItem}
                      onClick={() =>
                        onOpenRequest(aiWorkspaceRequestId(request), "script-vet")
                      }
                    >
                      <div className={pageStyles.requestInfo}>
                        <div className={pageStyles.requestTags}>
                          <span className={pageStyles.timeTag}>
                            <Clock size={11} />
                            {request.updatedAt
                              ? formatDistanceToNow(new Date(request.updatedAt), {
                                  addSuffix: true,
                                })
                              : "-"}
                          </span>
                        </div>
                        <p className={pageStyles.requestTitle}>
                          {trimmedScript(request.scripting?.concept || "") ||
                            trimmedScript(request.scripting?.title || "") ||
                            "Untitled script"}
                        </p>
                        {firstLine(request.masterPrompt || "") ? (
                          <p className={pageStyles.requestSubtitle}>
                            {firstLine(request.masterPrompt || "")}
                          </p>
                        ) : null}
                      </div>
                      <ArrowRight size={15} className={pageStyles.requestArrow} />
                    </button>
                  ))}
                </div>
              )
            ),
          },
        ]}
      />
    </div>
  );
}
